import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Settings,
  Search,
  Globe,
  FileText,
  Database,
  Rocket,
  Target,
  Mail,
  CreditCard,
  ListTree,
  BrainCircuit,
  Plus,
  ChevronRight,
  Users,
  Hash,
  ChevronLeft,
  Menu,
  LogOut,
  UserCog,
  Calendar,
  Lightbulb,
  BookOpen,
  Briefcase,
  Landmark,
  Lock,
  BarChart3,
  Clock,
  Shield,
  X,
  FolderOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import ProfileAvatar from "@/components/ProfileAvatar";

interface DashboardNavProps {
  collapsed: boolean;
  onToggle: () => void;
  isMobile?: boolean;
}

const mainNavItems: any[] = [];

const sidebarSections = [
  {
    id: "research",
    title: "Research",
    icon: Database,
    items: [
      {
        id: "analyze",
        title: "Analyze Website",
        icon: Globe,
        path: "analyze",
      },
      {
        id: "research",
        title: "Research Results",
        icon: Search,
        path: "research",
        subItems: [
          {
            id: "product",
            title: "Product",
            icon: FileText,
            path: "research/product",
          },
          {
            id: "audience",
            title: "Audience",
            icon: Users,
            path: "research/audience",
          },
          {
            id: "keywords",
            title: "Keywords",
            icon: Hash,
            path: "research/keywords",
          },
        ]
      },
      {
        id: "marketing-vault",
        title: "Marketing Vault",
        icon: Landmark,
        path: "research/marketing-vault",
      },
    ],
  },
  {
    id: "content",
    title: "Content Engine",
    icon: FileText,
    items: [
      {
        id: "topics",
        title: "Generate Content",
        icon: ListTree,
        path: "topics",
      },
      {
        id: "generated",
        title: "Content Library",
        icon: FileText,
        path: "content",
      },
      {
        id: "social-topics",
        title: "Social Calendar",
        icon: Calendar,
        path: "social-topics",
      },
    ],
  },
  {
    id: "launch",
    title: "Launch tools",
    icon: Rocket,
    items: [
      {
        id: "directories",
        title: "Directories",
        icon: FolderOpen,
        path: "directories",
      },
      {
        id: "producthunt",
        title: "Product Hunt",
        icon: Target,
        path: "producthunt",
      },
      {
        id: "cold-outreach",
        title: "Cold Outreach",
        icon: Mail,
        path: "cold-outreach",
      },
      {
        id: "competitor-content",
        title: "Competitor content",
        icon: Lightbulb,
        path: "competitor-content",
      },
    ],
  },
];

export function DashboardNav({ collapsed, onToggle, isMobile = false }: DashboardNavProps) {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    // Initialize with Research expanded, but other sections collapsed
    return sidebarSections.reduce((acc, section) => {
      // Only Research is expanded by default
      acc[section.id] = section.id === "research";
      return acc;
    }, {} as Record<string, boolean>);
  });
  const { signOut } = useAuth();

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });
      return data;
    }
  });

  // Store the current project ID in localStorage when it changes
  useEffect(() => {
    if (id) {
      localStorage.setItem('lastProjectId', id);
    }
  }, [id]);

  // Add state to track expanded submenus
  const [expandedSubmenus, setExpandedSubmenus] = useState<Record<string, boolean>>({});

  // Toggle section expansion
  const toggleSection = (sectionId: string) => {
    if (!collapsed) {
      setExpandedSections(prev => ({
        ...prev,
        [sectionId]: !prev[sectionId]
      }));
    }
  };

  // Toggle submenu expansion
  const toggleSubmenu = (itemId: string, e: React.MouseEvent) => {
    if (!collapsed) {
      e.stopPropagation(); // Prevent triggering parent clicks
      setExpandedSubmenus(prev => ({
        ...prev,
        [itemId]: !prev[itemId]
      }));
    }
  };

  // Check if a section contains the active path
  const isSectionActive = (section: any) => {
    return section.items.some((item: any) => {
      if (item.subItems) {
        return item.subItems.some((subItem: any) => 
          location.pathname === `/projects/${id}/${subItem.path}`
        );
      }
      return location.pathname === `/projects/${id}/${item.path}`;
    });
  };

  const handleProjectSelect = (projectId: string) => {
    // When explicitly selecting a project, update both current and last project IDs
    localStorage.setItem('currentProjectId', projectId);
    localStorage.setItem('lastProjectId', projectId);
    
    // Always navigate to the project dashboard when selecting a project
    navigate(`/projects/${projectId}`);
  };

  // If we're on the settings page and there's a stored project context, restore it when leaving
  useEffect(() => {
    if (!id && location.pathname !== '/settings') {
      const currentProjectId = localStorage.getItem('currentProjectId');
      if (currentProjectId) {
        // Prevent infinite navigation loops by checking if we're already trying to navigate to a project
        const isNavigatingToProject = location.pathname.includes('/projects/');
        const targetPath = isNavigatingToProject ? null : `/projects/${currentProjectId}`;
        
        if (targetPath) {
          navigate(targetPath, { replace: true });
        }
      }
    }
  }, [id, location.pathname, navigate]);

  return (
    <aside className={cn(
      "h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out",
      // Only use fixed positioning and z-index on desktop
      !isMobile && "fixed top-0 left-0 z-30",
      // Width adjustments
      collapsed ? "w-20" : "w-64"
    )}>
      <div className="flex h-screen flex-col">
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700">
          {!collapsed && (
            <Link
              to={id ? `/projects/${id}/playbooks` : "/playbooks"}
              className="text-xl font-bold gradient-text hover:opacity-90 transition-opacity"
              title="Go to Playbooks"
            >
              LaunchGuide
            </Link>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onToggle}
            className={cn(
              "w-8 h-8 rounded-full",
              collapsed ? "mx-auto" : "ml-auto"
            )}
          >
            {/* Show X on mobile when in sheet, show normal icons on desktop */}
            {isMobile ? <X size={18} /> : (collapsed ? <Menu size={18} /> : <ChevronLeft size={18} />)}
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {mainNavItems.map((item) => {
              const isActive = item.path === "" 
                ? location.pathname === `/projects/${id}` 
                : location.pathname === `/projects/${id}/${item.path}`;
              
              return (
                <Link
                  key={item.title}
                  to={`/projects/${id}/${item.path}`}
                  className={cn(
                    "flex items-center px-3 py-2 text-sm rounded-md transition-colors",
                    isActive 
                      ? "bg-blue-50 text-blue-600 font-medium dark:bg-blue-900/30 dark:text-blue-400" 
                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50",
                    collapsed ? "justify-center" : "",
                    item.path === "playbooks" && "playbook-link"
                  )}
                >
                  <item.icon className={cn("h-4 w-4", 
                    isActive 
                      ? "text-blue-600 dark:text-blue-400" 
                      : "text-gray-500 dark:text-gray-500",
                    collapsed ? "" : "mr-2"
                  )} />
                  {!collapsed && (
                    <div className="flex items-center">
                      <span>{item.title}</span>
                    </div>
                  )}
                </Link>
              );
            })}
            {/* Playbooks link - back to main playbooks page */}
            {id && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`/projects/${id}/playbooks`}
                      className={cn(
                        "flex items-center px-3 py-2 text-sm rounded-md transition-colors",
                        location.pathname === `/projects/${id}/playbooks`
                          ? "bg-blue-50 text-blue-600 font-medium dark:bg-blue-900/30 dark:text-blue-400"
                          : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50",
                        collapsed ? "justify-center" : "",
                        "playbook-link mt-2"
                      )}
                    >
                      <BookOpen className={cn(
                        "h-4 w-4",
                        location.pathname === `/projects/${id}/playbooks`
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-500 dark:text-gray-500",
                        collapsed ? "" : "mr-2"
                      )} />
                      {!collapsed && (
                        <div className="flex items-center">
                          <span>Playbooks</span>
                        </div>
                      )}
                    </Link>
                  </TooltipTrigger>
                  {collapsed && (
                    <TooltipContent side="right">Playbooks</TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          
          <div className="mt-6 space-y-4">
            {sidebarSections.map((section) => {
              const sectionActive = isSectionActive(section);
              const isExpanded = expandedSections[section.id];
              
              return (
                <div key={section.id}>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSection(section.id);
                          }}
                          data-section={section.id}
                          className={cn(
                            "w-full flex items-center justify-between rounded-md px-3 py-2 text-sm font-medium transition-colors",
                            isSectionActive(section) 
                              ? "text-blue-600 dark:text-blue-400" 
                              : "text-gray-700 dark:text-gray-400",
                            collapsed ? "justify-center" : ""
                          )}
                        >
                          <div className="flex items-center">
                            <section.icon className={cn("flex-shrink-0", collapsed ? "h-5 w-5" : "h-5 w-5 mr-3")} />
                            {!collapsed && (
                              <div className="flex items-center gap-2">
                                <span>{section.title}</span>
                                {(section as any).isBeta && (
                                  <span className="px-1.5 py-0.5 text-[10px] bg-orange-100 text-orange-600 rounded-full font-medium border border-orange-200">
                                    BETA
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {!collapsed && (
                            <ChevronRight className={cn(
                              "h-4 w-4 transition-transform", 
                              isExpanded ? "rotate-90" : ""
                            )} />
                          )}
                        </button>
                      </TooltipTrigger>
                      {collapsed && (
                        <TooltipContent side="right">
                          <div className="relative flex items-center gap-2">
                            {section.title}
                            {(section as any).isBeta && (
                              <span className="px-1.5 py-0.5 text-[10px] bg-orange-100 text-orange-600 rounded-full font-medium border border-orange-200">
                                BETA
                              </span>
                            )}
                            {(section as any).isNew && (
                              <span className="absolute -top-2.5 -right-4 text-[12px] text-red-500 transform rotate-12 font-bold" style={{
                                fontFamily: "inherit",
                                textShadow: '0 0 1px rgba(220, 38, 38, 0.5)'
                              }}>
                                NEW!
                              </span>
                            )}
                          </div>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  
                  {!collapsed && isExpanded && (
                    <div className={`mt-1 space-y-1 pl-10 ${section.id}-items`}>
                      {section.items.map((item) => {
                        const itemPath = `/projects/${id}/${item.path}`;
                        const isItemActive = item.subItems 
                          ? item.subItems.some((subItem: any) => location.pathname === `/projects/${id}/${subItem.path}`)
                          : location.pathname === itemPath;

                        return (
                          <div key={item.id} className="py-1">
                            {item.subItems ? (
                              <div>
                                <button
                                  onClick={(e) => toggleSubmenu(item.id, e)}
                                  className={cn(
                                    "w-full flex items-center justify-between px-3 py-2 text-sm rounded-md transition-colors",
                                    isItemActive 
                                      ? "text-blue-600 font-medium dark:text-blue-400" 
                                      : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300",
                                    item.id === "research" && "research-results-link"
                                  )}
                                >
                                  <div className="flex items-center">
                                    <item.icon className={cn("mr-2 h-4 w-4",
                                      isItemActive 
                                        ? "text-blue-600 dark:text-blue-400" 
                                        : "text-gray-500 dark:text-gray-500"
                                    )} />
                                    <span>{item.title}</span>
                                  </div>
                                  <ChevronRight
                                    className={cn(
                                      "h-4 w-4 transition-transform",
                                      expandedSubmenus[item.id] ? "rotate-90" : ""
                                    )}
                                  />
                                </button>

                                {/* Submenu items */}
                                {expandedSubmenus[item.id] && item.subItems.map((subItem: any) => {
                                  const subItemPath = `/projects/${id}/${subItem.path}`;
                                  const isSubItemActive = location.pathname === subItemPath;

                                  return (
                                    <Link
                                      key={subItem.id}
                                      to={subItemPath}
                                      className={cn(
                                        "flex items-center pl-9 pr-3 py-2 text-sm rounded-md transition-colors mt-1",
                                        isSubItemActive 
                                          ? "text-blue-600 font-medium dark:text-blue-400" 
                                          : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                                      )}
                                    >
                                      <subItem.icon className={cn("mr-2 h-4 w-4",
                                        isSubItemActive 
                                          ? "text-blue-600 dark:text-blue-400" 
                                          : "text-gray-500 dark:text-gray-500"
                                      )} />
                                      <span>{subItem.title}</span>
                                    </Link>
                                  );
                                })}
                              </div>
                            ) : (
                              <Link
                                to={itemPath}
                                className={cn(
                                  "flex items-center px-3 py-2 text-sm rounded-md transition-colors",
                                  isItemActive 
                                    ? "text-blue-600 font-medium dark:text-blue-400" 
                                    : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300",
                                  item.id === "research" && "research-link",
                                  item.id === "marketing-vault" && "marketing-vault-link",
                                  item.id === "topics" && "topics-link",
                                  item.id === "generated" && "content-link"
                                )}
                              >
                                <item.icon className={cn("mr-2 h-4 w-4",
                                  isItemActive 
                                    ? "text-blue-600 dark:text-blue-400" 
                                    : "text-gray-500 dark:text-gray-500"
                                )} />
                                <span>{item.title}</span>
                              </Link>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {collapsed && (
                    <div className="mt-1 space-y-1 flex flex-col items-center">
                      {section.items.map((item) => {
                        const isItemActive = item.path ? location.pathname === `/projects/${id}/${item.path}` : false;
                        const hasSubItems = item.subItems && item.subItems.length > 0;
                        const isSubItemActive = hasSubItems && item.subItems.some(
                          (subItem: any) => location.pathname === `/projects/${id}/${subItem.path}`
                        );
                        
                        // If this item has subitems like Reddit, use the toggle functionality even in collapsed mode
                        const itemComponent = hasSubItems ? (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleSubmenu(item.id, e);
                            }}
                            className={cn(
                              "flex items-center justify-center w-8 h-8 rounded-md mt-1",
                              (isItemActive || isSubItemActive)
                                ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" 
                                : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/50"
                            )}
                          >
                            <item.icon className="h-4 w-4" />
                          </button>
                        ) : (
                          <Link
                            to={`/projects/${id}/${item.path}`}
                            className={cn(
                              "flex items-center justify-center w-8 h-8 rounded-md mt-1",
                              (isItemActive || isSubItemActive)
                                ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" 
                                : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/50"
                            )}
                          >
                            <item.icon className="h-4 w-4" />
                          </Link>
                        );
                        
                        return (
                          <TooltipProvider key={item.id} delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {itemComponent}
                              </TooltipTrigger>
                              <TooltipContent side="right">
                                <div>
                                  <div>{item.title}</div>
                                  {hasSubItems && expandedSubmenus[item.id] && (
                                    <div className="border-t border-gray-200 dark:border-gray-700 mt-1 pt-1 space-y-1">
                                      {item.subItems.map((subItem: any) => (
                                        <Link 
                                          key={subItem.id}
                                          to={`/projects/${id}/${subItem.path}`}
                                          className="flex items-center text-xs py-1 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <subItem.icon className="mr-1 h-3 w-3" />
                                          <span>{subItem.title}</span>
                                        </Link>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        <div className="mt-auto border-t border-gray-200 dark:border-gray-700 p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={cn(
                  "w-full justify-start gap-3 px-3 py-2 rounded-md text-left",
                  collapsed ? "justify-center" : ""
                )}
              >
                <ProfileAvatar size="sm" />
                {!collapsed && (
                  <div className="flex-1 min-w-0 text-left">
                    <div className="text-sm font-medium truncate">
                      {projects?.find(p => p.id === id)?.name || projects?.[0]?.name || 'Your Workspace'}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      Pro Plan
                    </div>
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[280px]">
              <div className="p-2">
                <div className="text-xs font-medium mb-2 text-gray-500 dark:text-gray-400">
                  Your Projects
                </div>
                {projects?.map((project) => (
                  <DropdownMenuItem key={project.id} onClick={() => handleProjectSelect(project.id)}>
                    <div className="flex items-center gap-3 w-full py-1">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-blue-500 flex items-center justify-center text-white font-medium text-sm">
                        {project.name?.charAt(0) || 'P'}
                      </div>
                      <span className="truncate">{project.name}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
                <Separator className="my-2" />
                <DropdownMenuItem>
                  <Link
                    to="/projects"
                    className="flex items-center gap-2 w-full text-blue-600 dark:text-blue-400"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Create New Project</span>
                  </Link>
                </DropdownMenuItem>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </aside>
  );
}
